<script setup lang="ts">
import { onBeforeMount, ref } from 'vue'

import BetaOpportunityCard from '@/components/beta-dashboard/molecules/BetaOpportunityCard.vue'
import BetaPanelHeading from '@/components/beta-dashboard/molecules/BetaPanelHeading.vue'
import notification from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'

const betaStore = useBetaDashboardStore()
const exporting = ref(false)
const cdnUrl = String(import.meta.env.VITE_CDN_URL || '')

onBeforeMount(() => betaStore.loadOpportunities())

const exportCsv = async () => {
    if (!betaStore.opportunities.length) {
        notification.simple('Nenhuma mensagem para exportar.', 'warning')
        return
    }
    if (exporting.value) return

    exporting.value = true
    try {
        const { blob, fileName } = await betaStore.exportOpportunities()
        const objectUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = objectUrl
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(objectUrl)

        notification.simple('CSV exportado com sucesso.', 'success')
    } catch (error) {
        notification.simple(
            error instanceof Error ? error.message : 'Não foi possível exportar o CSV.',
            'error',
        )
    } finally {
        exporting.value = false
    }
}
</script>

<template>
    <div class="beta-opportunities">
        <BetaPanelHeading title="Oportunidades">
            <template #actions>
                <button
                    type="button"
                    class="beta-opportunities__export"
                    :disabled="betaStore.loadingOpportunities || !betaStore.opportunities.length || exporting"
                    @click="exportCsv"
                >
                    {{ exporting ? 'Exportando…' : 'Exportar CSV' }}
                </button>
            </template>
        </BetaPanelHeading>

        <div v-if="betaStore.loadingOpportunities" class="beta-opportunities__state">
            Carregando mensagens…
        </div>

        <div v-else-if="betaStore.opportunitiesError" class="beta-opportunities__state is-error">
            <span>{{ betaStore.opportunitiesError }}</span>
            <button type="button" @click="betaStore.loadOpportunities()">Tentar novamente</button>
        </div>

        <div v-else-if="!betaStore.opportunities.length" class="beta-opportunities__state">
            <strong>Nenhuma mensagem recebida</strong>
            <span>Quando alguém preencher um formulário do site, a mensagem aparecerá aqui.</span>
        </div>

        <template v-else>
            <div class="beta-opportunities__summary">
                <span>{{ betaStore.opportunities.length }}</span>
                {{ betaStore.opportunities.length === 1 ? 'mensagem encontrada' : 'mensagens encontradas' }}
            </div>

            <div class="beta-opportunities__list">
                <BetaOpportunityCard
                    v-for="opportunity in betaStore.opportunities"
                    :key="opportunity.id"
                    :opportunity="opportunity"
                    :cdn-url="cdnUrl"
                />
            </div>
        </template>
    </div>
</template>

<style scoped>
.beta-opportunities {
    display: grid;
    gap: 16px;
}

.beta-opportunities__export,
.beta-opportunities__state button {
    min-height: 38px;
    flex: none;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 14px;
    color: #fff;
    font-size: 12px;
    font-weight: 850;
}

.beta-opportunities__export:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.beta-opportunities__summary {
    color: #7b8ba0;
    font-size: 12px;
}

.beta-opportunities__summary span {
    color: #304762;
    font-size: 10px;
    font-weight: 900;
}

.beta-opportunities__list {
    display: grid;
    gap: 10px;
}

.beta-opportunities__state {
    display: grid;
    min-height: 260px;
    place-content: center;
    justify-items: center;
    gap: 7px;
    border: 1px dashed #dbe3ed;
    border-radius: 18px;
    background: #f9fbfd;
    padding: 24px;
    color: #7e8da2;
    font-size: 9px;
    text-align: center;
}

.beta-opportunities__state strong {
    color: #304863;
    font-size: 12px;
}

.beta-opportunities__state.is-error {
    color: #a34e4e;
}

@media (max-width: 600px) {
    .beta-opportunities__heading {
        align-items: stretch;
        flex-direction: column;
    }
}
</style>
